"use client";

import type { ICmsLink, Media } from "@mw/shared/payload-types";

import { easedGradientStops } from "@ui/lib/gradients";
import { clsx } from "clsx";
import { AnimatePresence } from "motion/react";
import * as m from "motion/react-m";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";

import { getImageProps, getLinkProps, getVideoUrl } from "@utilities/payload";
import { Link } from "./Link";
import IconChevronLeftMedium from "@ui/central-icons/icons/IconChevronLeftMedium";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import IconChevronRightMedium from "@ui/central-icons/icons/IconChevronRightMedium";
import Image from "next/image";
import { pxToRem } from "@ui/lib/utils";
import { MediaProgressBar } from "./MediaProgressBar";
import IconPauseFill from "@ui/central-icons/icons/IconPauseFill";
import IconPlayFill from "@ui/central-icons/icons/IconPlayFill";
import IconVolumeOffFill from "@ui/central-icons/icons/IconVolumeOffFill";
import IconVolumeFullFill from "@ui/central-icons/icons/IconVolumeFullFill";

export interface VideoPlayerVideo {
  title?: string | null;
  description?: string | null;
  media: Media | string | null;
  link?: ICmsLink;
  logo?: Media | string | null;
  image?: Media | string | null;
  hideTitle?: boolean | null;
}

export type VideoPlayerContentProps = {
  video: VideoPlayerVideo;
  videoRef?: React.RefObject<HTMLVideoElement | null>;
  isMuted: boolean;
  showControls: boolean;
  inView: boolean | undefined;
  enableOverlay?: boolean;
  /** Object-fit behavior for the video. Defaults to "cover". */
  objectFit?: "cover" | "contain";
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
  onInteraction: () => void;
  autoPlay?: boolean;
  toggleMute?: () => void;
  showProgressBar?: boolean;
  onNext?: () => void;
  onPrevious?: () => void;
  hideCtaOnMobile?: boolean;
  hideTextOnMobile?: boolean;
};

function VideoWithPlaceholder({
  objectFit,
  thumbnail,
  inView,
  ...props
}: React.ComponentPropsWithRef<"video"> & {
  objectFit?: "cover" | "contain";
  thumbnail?: Media | string | null;
  inView: boolean | undefined;
}) {
  const [wasPlaying, setWasPlaying] = useState(false);
  const [showThumbnail, setShowThumbnail] = useState(true);
  const thumbnailProps = getImageProps(thumbnail, { placeholder: true, fill: true });

  useEffect(() => {
    if (!wasPlaying) return;
    const timer = setTimeout(() => {
      setShowThumbnail(false);
    }, 300);
    return () => {
      clearTimeout(timer);
    };
  }, [wasPlaying]);

  return (
    <m.div
      initial={{ opacity: 0.6 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0.6 }}
      transition={{ duration: 0.4, ease: "easeInOut" }}
    >
      {inView && (
        <video
          className={clsx(
            "tw-absolute tw-inset-0 tw-w-full tw-h-full",
            objectFit === "contain" ? "tw-object-contain" : "tw-object-cover",
          )}
          {...props}
          onPlay={(...args) => {
            setWasPlaying(true);
            props.onPlay?.(...args);
          }}
        />
      )}

      {/* Static image overlay, hidden once the video has started playing */}
      {thumbnailProps && (
        <Image
          {...thumbnailProps}
          alt=''
          objectFit={objectFit}
          className={clsx(
            "tw-absolute tw-inset-0 tw-size-full tw-transition-opacity tw-duration-300",
            !showThumbnail && "tw-opacity-0",
          )}
          inert={!showThumbnail}
        />
      )}
    </m.div>
  );
}

export function VideoPlayerContent({
  video,
  videoRef,
  isMuted,
  showControls,
  inView,
  enableOverlay,
  objectFit = "cover",
  onPlay = () => {},
  onPause = () => {},
  onEnded = () => {},
  onInteraction,
  autoPlay = true,
  toggleMute,
  showProgressBar,
  onNext,
  onPrevious,
  hideCtaOnMobile = false,
  hideTextOnMobile = false,
}: VideoPlayerContentProps) {
  const [isPlaying, setIsPlaying] = useState(false);

  const videoUrl = video.media ? getVideoUrl(video.media) : null;

  if (!videoUrl) {
    return null;
  }

  const togglePlayPause = () => {
    if (videoRef?.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(() => {
          // Play might fail, but the onPause event will keep state accurate
        });
      }
    }
  };

  const handlePlay = () => {
    setIsPlaying(true);
    onPlay();
  };

  const handlePause = () => {
    setIsPlaying(false);
    onPause();
  };

  return (
    <>
      {/* Full-bleed video - only render when in view */}
      <AnimatePresence>
        <VideoWithPlaceholder
          key={videoUrl}
          inView={inView}
          ref={videoRef}
          src={videoUrl}
          objectFit={objectFit}
          muted={isMuted}
          playsInline
          autoPlay={autoPlay}
          onPlay={handlePlay}
          onPause={handlePause}
          onEnded={onEnded}
          thumbnail={video.image}
        />
      </AnimatePresence>

      <VideoPlayerControls
        isVisible={showControls}
        enableOverlay={enableOverlay}
        togglePlayPause={togglePlayPause}
        onInteraction={onInteraction}
        logo={video.logo}
        title={video.title}
        description={video.description}
        toggleMute={toggleMute}
        isMuted={isMuted}
        isPlaying={isPlaying}
        videoRef={videoRef}
        showProgressBar={showProgressBar}
        onNext={onNext}
        onPrevious={onPrevious}
        cta={video.link}
        hideCtaOnMobile={hideCtaOnMobile}
        hideTextOnMobile={hideTextOnMobile}
        hideTitle={video.hideTitle}
      />
    </>
  );
}

export function VideoPlayerControls({
  isVisible = true,
  enableOverlay = false,
  togglePlayPause,
  onInteraction,
  logo,
  title,
  description,
  toggleMute,
  isMuted,
  isPlaying,
  cta,
  videoRef,
  showProgressBar = false,
  onNext,
  onPrevious,
  hideCtaOnMobile = false,
  hideTextOnMobile = false,
  hideTitle = false,
}: {
  isVisible?: boolean;
  enableOverlay?: boolean;
  togglePlayPause?: () => void;
  onInteraction?: () => void;
  logo?: Media | string | null;
  title?: string | null;
  description?: string | null;
  toggleMute?: () => void;
  isMuted?: boolean;
  isPlaying?: boolean;
  cta?: ICmsLink;
  videoRef?: React.RefObject<HTMLVideoElement | null>;
  showProgressBar?: boolean;
  onNext?: () => void;
  onPrevious?: () => void;
  hideCtaOnMobile?: boolean;
  hideTextOnMobile?: boolean;
  hideTitle?: boolean | null;
}) {
  const t = useTranslations("ClientKeys.video_rotation_demo");
  const tGlobal = useTranslations("ClientKeys.Globals");
  const overlayGradient = enableOverlay
    ? `linear-gradient(to bottom, ${easedGradientStops("rgba(0, 0, 0, 0.6)", "rgba(0, 0, 0, 0)")})`
    : undefined;
  const ctaLink = getLinkProps(cta);
  const logoProps = getImageProps(logo);

  return (
    <>
      {/* Gradient overlay for improved text readability */}
      {enableOverlay && (
        <div
          className='tw-absolute tw-inset-0 tw-pointer-events-none tw-z-[1] md:tw-h-1/3'
          style={{ background: overlayGradient }}
        />
      )}

      {/* Mobile-only invisible button to show controls on tap */}
      {onInteraction && (
        <button
          type='button'
          onClick={() => {
            if (!isPlaying) {
              togglePlayPause?.();
            }
            onInteraction();
          }}
          className='tw-absolute tw-inset-0 tw-w-full tw-h-full tw-bg-transparent tw-z-40 tw-outline-none focus-visible:tw-outline-focus focus-visible:-tw-outline-offset-4 tw-rounded-3xl md:tw-hidden'
          aria-label={t("tap_to_show_controls")}
          inert={isVisible}
        />
      )}

      <div className='tw-absolute tw-z-50 tw-top-0 tw-inset-x-0 tw-flex tw-pointer-events-none'>
        {/* Title and description */}
        {((title && !hideTitle) || description || logoProps) && (
          <div
            className={clsx(
              "tw-max-w-xl tw-p-4 md:tw-p-8 tw-pointer-events-auto",
              hideTextOnMobile && "tw-hidden md:tw-block",
            )}
          >
            {logoProps && <Image {...logoProps} alt='' className='tw-h-10 tw-mb-4 tw-brightness-0 tw-invert' />}
            {title && !hideTitle && <p className='tw-type-xl tw-text-white tw-mb-3.5'>{title}</p>}
            {description && <p className='tw-type-base tw-text-white/60'>{description}</p>}
          </div>
        )}

        <div className='tw-ml-auto tw-flex tw-gap-x-4 tw-p-2'>
          {(onNext || onPrevious) && (
            <div className='tw-flex tw-gap-x-2'>
              {onPrevious && (
                <button
                  type='button'
                  onClick={onPrevious}
                  aria-label={tGlobal("previous")}
                  className={clsx(
                    "tw-pointer-events-auto tw-bg-black/20 tw-backdrop-blur-xl tw-text-white hover:tw-bg-black/30 tw-rounded-full tw-size-10 tw-items-center tw-justify-center tw-transition-colors tw-hidden md:tw-flex tw-outline-none focus-visible:tw-outline-focus",
                  )}
                >
                  <IconChevronLeftMedium className={clsx("tw-size-4.5", iconStrokeFix)} />
                </button>
              )}
              {onNext && (
                <button
                  type='button'
                  onClick={onNext}
                  aria-label={tGlobal("next")}
                  className={clsx(
                    "tw-pointer-events-auto tw-bg-black/20 tw-backdrop-blur-xl tw-text-white hover:tw-bg-black/30 tw-rounded-full tw-size-10 tw-items-center tw-justify-center tw-transition-colors tw-hidden md:tw-flex tw-outline-none focus-visible:tw-outline-focus",
                  )}
                >
                  <IconChevronRightMedium className={clsx("tw-size-4.5", iconStrokeFix)} />
                </button>
              )}
            </div>
          )}
          {toggleMute && (
            <button
              type='button'
              onClick={toggleMute}
              className={clsx(
                "tw-pointer-events-auto tw-bg-black/20 tw-backdrop-blur-xl tw-text-white hover:tw-bg-black/30 tw-rounded-full tw-size-10 tw-flex tw-items-center tw-justify-center tw-transition-colors tw-outline-none focus-visible:tw-outline-focus",
                !isVisible && !isMuted && "md:tw-opacity-100 tw-opacity-0",
              )}
              aria-label={isMuted ? tGlobal("unmute") : tGlobal("mute")}
            >
              {isMuted ? <IconVolumeOffFill className='tw-size-4.5' /> : <IconVolumeFullFill className='tw-size-4.5' />}
            </button>
          )}
        </div>
      </div>

      {togglePlayPause && (
        <button
          type='button'
          onClick={togglePlayPause}
          className={clsx(
            "tw-absolute tw-z-50 tw-bg-black/20 tw-backdrop-blur-xl tw-text-white hover:tw-bg-black/30 tw-flex tw-items-center tw-justify-center tw-rounded-3xl tw-transition-colors tw-outline-none focus-visible:tw-outline-focus",
            "tw-h-10 tw-w-14",
            "tw-top-1/2 tw-left-1/2 -tw-translate-x-1/2 -tw-translate-y-1/2",
            "md:tw-left-8 md:tw-top-auto md:tw-bottom-8 md:tw-translate-x-0 md:tw-translate-y-0",
            !isVisible && isPlaying
              ? "md:tw-opacity-100 tw-opacity-0 tw-pointer-events-none md:tw-pointer-events-auto"
              : "tw-pointer-events-auto",
          )}
          aria-label={isPlaying ? tGlobal("pause") : tGlobal("play")}
        >
          {isPlaying ? <IconPauseFill className='tw-size-4' /> : <IconPlayFill className='tw-size-4' />}
        </button>
      )}

      <div
        className={clsx(
          "tw-absolute tw-z-50 tw-bottom-0 tw-inset-x-0 tw-py-4 tw-px-8 md:tw-p-8 tw-pointer-events-none tw-grid md:tw-grid-cols-[1fr_auto_1fr] tw-items-center tw-gap-x-8 md:tw-gap-x-10",
          ctaLink ? "tw-grid-cols-[1fr_auto] tw-pr-4" : "tw-grid-cols-1",
        )}
      >
        {togglePlayPause && <div className='tw-hidden md:tw-block tw-h-10 tw-w-14' />}

        {showProgressBar && videoRef && (
          <div
            className={clsx(
              "tw-min-w-0 tw-flex tw-pointer-events-auto tw-transition-opacity",
              !isVisible && "md:tw-opacity-100 tw-opacity-0",
            )}
          >
            <div style={{ width: pxToRem(600) }}>
              <MediaProgressBar mediaRef={videoRef} inverse />
            </div>
          </div>
        )}

        {ctaLink && (
          <div className='tw-col-start-2 md:tw-col-start-3 tw-flex tw-justify-end'>
            <div className='tw-pointer-events-auto'>
              {!hideCtaOnMobile && (
                <div className='tw-contents sm:tw-hidden'>
                  <Link {...ctaLink} asButton buttonVariants={{ variant: "elevated", size: "sm" }} />
                </div>
              )}
              <div className='tw-hidden sm:tw-contents'>
                <Link {...ctaLink} asButton buttonVariants={{ variant: "elevated", size: "md" }} />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
